import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Reveal, Eyebrow } from "@/components/Reveal";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";

const initialForm = {
  company_name: "", contact_person: "", email: "", phone: "",
  country: "", products: "", message: "",
};

const inputCls =
  "bg-midnight border-[#1E293B] text-[#F4F6F8] placeholder:text-[#94A3B8]/50 focus-visible:ring-gold/60 h-12 rounded-sm";

export const SupplierSection = () => {
  const { t } = useLang();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/suppliers", form);
      toast.success(t.supplier.success);
      setForm(initialForm);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.supplier.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="suppliers" data-testid="supplier-section" className="py-24 md:py-32 bg-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <Reveal>
          <Eyebrow>{t.supplier.eyebrow}</Eyebrow>
          <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#F4F6F8] mb-6">
            {t.supplier.title}
          </h2>
          <p className="text-base text-[#94A3B8] leading-relaxed mb-8 max-w-lg">
            {t.supplier.body}
          </p>
          <ul className="space-y-4">
            {t.supplier.bullets.map((item) => (
              <li key={item} className="flex items-center gap-3 text-base text-[#F4F6F8]">
                <span className="w-1.5 h-1.5 bg-gold rotate-45 shrink-0" /> {item}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.12}>
          <form
            data-testid="supplier-form"
            onSubmit={submit}
            className="bg-midnight border border-[#1E293B] p-8 md:p-10 space-y-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="sup-company" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.supplier.labels.company}</Label>
                <Input id="sup-company" data-testid="supplier-company-input" required value={form.company_name} onChange={set("company_name")} className={inputCls} placeholder={t.supplier.placeholders.company} />
              </div>
              <div>
                <Label htmlFor="sup-contact" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.supplier.labels.contact}</Label>
                <Input id="sup-contact" data-testid="supplier-contact-input" required value={form.contact_person} onChange={set("contact_person")} className={inputCls} placeholder={t.supplier.placeholders.contact} />
              </div>
              <div>
                <Label htmlFor="sup-email" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.supplier.labels.email}</Label>
                <Input id="sup-email" data-testid="supplier-email-input" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder={t.supplier.placeholders.email} />
              </div>
              <div>
                <Label htmlFor="sup-phone" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.supplier.labels.phone}</Label>
                <Input id="sup-phone" data-testid="supplier-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder={t.supplier.placeholders.phone} />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="sup-country" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.supplier.labels.country}</Label>
                <Input id="sup-country" data-testid="supplier-country-input" required value={form.country} onChange={set("country")} className={inputCls} placeholder={t.supplier.placeholders.country} />
              </div>
            </div>
            <div>
              <Label htmlFor="sup-products" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.supplier.labels.products}</Label>
              <Input id="sup-products" data-testid="supplier-products-input" required value={form.products} onChange={set("products")} className={inputCls} placeholder={t.supplier.placeholders.products} />
            </div>
            <div>
              <Label htmlFor="sup-message" className="text-xs uppercase tracking-wider text-[#94A3B8]">{t.supplier.labels.message}</Label>
              <Textarea id="sup-message" data-testid="supplier-message-input" value={form.message} onChange={set("message")} rows={4} className={`${inputCls} h-auto`} placeholder={t.supplier.placeholders.message} />
            </div>
            <button
              data-testid="supplier-submit-button"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-midnight font-semibold px-8 py-4 rounded-full hover:bg-gold-light hover:text-midnight transition-[background-color,transform] duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
              {loading ? t.supplier.submitting : t.supplier.submit}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
